import { Controller } from "@hotwired/stimulus"

// Manage scheduled reports — list / toggle / delete / send-test.
// Mirrors portfolio_alert_manage_controller.js. The create/edit form
// lives in the separate portfolio-schedule-report controller (same
// slide-over used from the Export modal path).
export default class extends Controller {
  static targets = [
    "summary", "list", "empty", "loading", "errors",
    "rowTemplate", "deleteName"
  ]
  static values = { indexUrl: String }

  connect() {
    this._reports = []
    this._pendingDelete = null
    this._onOpen = this._onOpen.bind(this)
    this._onRefresh = this._onRefresh.bind(this)
    document.addEventListener("portfolio-scheduled-reports-manage:open", this._onOpen)
    document.addEventListener("portfolio-scheduled-reports-manage:refresh", this._onRefresh)
  }

  disconnect() {
    document.removeEventListener("portfolio-scheduled-reports-manage:open", this._onOpen)
    document.removeEventListener("portfolio-scheduled-reports-manage:refresh", this._onRefresh)
  }

  _onOpen() {
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-scheduled-reports-manage" } }))
    this.load()
  }

  _onRefresh() {
    this.load()
  }

  async load() {
    this._showLoading()
    try {
      const res = await fetch(this.indexUrlValue, {
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      this._reports = json.reports || []
      this._render()
    } catch (err) {
      console.error("Load scheduled reports failed", err)
      this._renderError("Could not load scheduled reports.")
    }
  }

  _render() {
    this.loadingTarget.classList.add("hidden")
    this.errorsTarget.classList.add("hidden")

    if (this._reports.length === 0) {
      this.listTarget.classList.add("hidden")
      this.emptyTarget.classList.remove("hidden")
      this.summaryTarget.textContent = "No reports scheduled"
      return
    }

    this.emptyTarget.classList.add("hidden")
    this.listTarget.classList.remove("hidden")
    const enabledCount = this._reports.filter(r => r.enabled).length
    this.summaryTarget.textContent = `${this._reports.length} report${this._reports.length === 1 ? "" : "s"} · ${enabledCount} active`

    this.listTarget.innerHTML = ""
    this._reports.forEach(report => this._appendRow(report))
  }

  _appendRow(report) {
    const node = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    node.dataset.id = report.id

    const toggle = node.querySelector('[data-role="toggle"]')
    toggle.checked = !!report.enabled
    toggle.addEventListener("change", () => this._toggle(report.id))

    node.querySelector('[data-role="name"]').textContent = report.name
    node.querySelector('[data-role="cadence"]').textContent = report.cadence_label

    const disabledBadge = node.querySelector('[data-role="disabled-badge"]')
    disabledBadge.classList.toggle("hidden", !!report.enabled)

    const recipients = node.querySelector('[data-role="recipients"]')
    const preview = report.recipients.slice(0, 3).join(", ")
    const extra = report.recipient_count > 3 ? ` + ${report.recipient_count - 3} more` : ""
    recipients.textContent = `${preview}${extra}`

    const meta = node.querySelector('[data-role="meta"]')
    const bits = []
    if (report.timezone) bits.push(report.timezone)
    if (report.filter_preset_name) bits.push(`Preset: ${report.filter_preset_name}`)
    if (report.last_sent_at) bits.push(`Last sent ${this._relativeDate(report.last_sent_at)}`)
    else bits.push("Not yet delivered")
    meta.textContent = bits.join(" · ")

    this.listTarget.appendChild(node)
  }

  async _toggle(id) {
    try {
      const res = await fetch(`${this.indexUrlValue}/${id}/toggle`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrf(), "Accept": "application/json" }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      await this.load()
    } catch (err) {
      console.error("Toggle failed", err)
      this._renderError("Could not toggle this report.")
    }
  }

  openNew(event) {
    if (event) event.preventDefault()
    document.getElementById("portfolio-scheduled-reports-manage-modal")?.classList.add("hidden")
    document.body.style.overflow = ""
    const modal = document.getElementById("portfolio-schedule-report-modal")
    if (modal) {
      modal.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  async sendTest(event) {
    event.preventDefault()
    const id = event.currentTarget.closest("[data-id]").dataset.id
    const btn = event.currentTarget
    btn.disabled = true
    try {
      const res = await fetch(`${this.indexUrlValue}/${id}/send_test`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrf(), "Accept": "application/json" }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      this._toast("Test delivery queued", "notice")
    } catch (err) {
      console.error("Test delivery failed", err)
      this._toast("Could not queue a test delivery", "alert")
    } finally {
      btn.disabled = false
    }
  }

  confirmDelete(event) {
    event.preventDefault()
    const id = event.currentTarget.closest("[data-id]").dataset.id
    const report = this._reports.find(r => String(r.id) === String(id))
    if (!report) return
    this._pendingDelete = id
    this.deleteNameTarget.textContent = report.name
    document.getElementById("portfolio-scheduled-reports-delete-modal")?.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  async performDelete() {
    if (!this._pendingDelete) return
    const id = this._pendingDelete
    try {
      const res = await fetch(`${this.indexUrlValue}/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrf(), "Accept": "application/json" }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      document.getElementById("portfolio-scheduled-reports-delete-modal")?.classList.add("hidden")
      this._pendingDelete = null
      await this.load()
      this._toast("Scheduled report deleted", "notice")
    } catch (err) {
      console.error("Delete failed", err)
      this._toast("Could not delete this report", "alert")
    }
  }

  _csrf() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  _showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.emptyTarget.classList.add("hidden")
    this.listTarget.classList.add("hidden")
    this.errorsTarget.classList.add("hidden")
    this.summaryTarget.textContent = "Loading…"
  }

  _renderError(msg) {
    this.errorsTarget.textContent = msg
    this.errorsTarget.classList.remove("hidden")
    this.loadingTarget.classList.add("hidden")
  }

  _relativeDate(iso) {
    const d = new Date(iso)
    const diff = (Date.now() - d.getTime()) / 1000
    if (diff < 60) return "just now"
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
    return d.toLocaleDateString()
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }
}
